import * as borogove from "borogove";
import { Alert, MenuList, CircularProgress, Tabs, Tab } from "@mui/material";
import { Await, useNavigate, useOutletContext } from "react-router";
import { Suspense, useState, useEffect } from "react";
import {
	Avatar,
	ChatContainer,
	ConversationHeader,
	MessageList,
} from "@chatscope/chat-ui-kit-react";

import { AvatarWithPlaceholder } from "./AvatarWithPlaceholder";
import { CommandMenuInner } from "./CommandMenu";
import { CommandSession } from "./CommandSession";
import { FormTable } from "./Form";
import { useEffectEvent, ChatExtras } from "../util";

const LogItem = ({
	form,
	data,
}: {
	form: borogove.FormSection;
	data: FormData;
}) => {
	return (
		<section>
			{form.title() && <h1>{form.title()}</h1>}
			{form instanceof borogove.Form && form.url() && (
				<a href={form.url() ?? undefined}>{form.url()}</a>
			)}
			{form.items().map((item) => (
				<>
					{item.text && item.status === "success" && (
						<Alert severity="success">{item.text}</Alert>
					)}
					{item.text && item.status === "info" && (
						<Alert severity="info">{item.text}</Alert>
					)}
					{item.text && item.status === "warn" && (
						<Alert severity="warning">{item.text}</Alert>
					)}
					{item.text && item.status === "error" && (
						<Alert severity="error">{item.text}</Alert>
					)}
					{item.text && !item.status && <p>{item.text}</p>}
					{item.field && (
						<div className="field">
							<label>{item.field.label}</label>
							<span>
								{(data.getAll(item.field.name).length > 0
									? data.getAll(item.field.name)
									: item.field.value
								).map((v, idx) =>
									item.field?.datatype === "xs:anyURI" ? (
										<a key={idx} href={"" + v}>
											{"" + v}
										</a>
									) : (
										<span key={idx}>{"" + v}</span>
									),
								)}
							</span>
						</div>
					)}
					{item.tableHeader && item.tableRows && (
						<FormTable header={item.tableHeader} rows={item.tableRows} />
					)}
					{item.section && <LogItem form={item.section} data={data} />}
				</>
			))}
		</section>
	);
};

export const AppChat = () => {
	const navigate = useNavigate();
	const { active, activeChat, getChatExtras, setChatExtras } =
		useOutletContext<{
			active: boolean;
			activeChat: borogove.Chat;
			getChatExtras: (id: string) => ChatExtras | undefined;
			setChatExtras: (id: string, d: Partial<ChatExtras>) => void;
		}>();

	const [commandsChatId, setCommandsChatId] = useState("");
	const [commands, setCommands] = useState(
		Promise.resolve([] as borogove.Command[]),
	);
	const [commandSessions, setCommandSessions] = useState<
		Promise<borogove.CommandSession>[]
	>([]);
	const [tabNames, setTabNames] = useState<string[]>([]);
	const [tabIndex, setTabIndex] = useState(0);
	const [log, setLog] = useState<
		{ id: string; form: borogove.Form; data: FormData }[]
	>([]);

	const newChat = useEffectEvent(() => {
		setCommandsChatId(activeChat.chatId);
		setCommands(activeChat.commands());

		const extras = getChatExtras(activeChat.chatId);
		setLog(extras?.commandLog ?? []);
		setTabIndex(extras?.tabIndex ?? 0);
		setTabNames(extras?.tabNames ?? []);
		const sessions = extras?.commandSessions ?? [];
		setCommandSessions(sessions);
		sessions.forEach((session, idx) =>
			session.then((s) =>
				setTabNames((existing) =>
					existing.map((value, i) => (i === idx ? s.name : value)),
				),
			),
		);
	});
	useEffect(() => {
		newChat();
	}, [activeChat.chatId]);

	useEffect(() => {
		setChatExtras(commandsChatId, { commandSessions, tabNames, tabIndex });
	}, [commandsChatId, commandSessions, tabNames, tabIndex, setChatExtras]);

	const reloadCommands = () => {
		setCommands(activeChat.commands());
	};

	const addCommandSession = (
		name: string,
		session: Promise<borogove.CommandSession>,
	) => {
		setTabNames((existing) => existing.concat([name]));
		setCommandSessions((existing) => {
			const idx = existing.length;
			setTabIndex(idx + 1);
			session.then((s) => {
				setTabNames((existing) =>
					existing.map((value, i) => (i === idx ? s.name : value)),
				);
				if (s.forms[0]) logStep(s.forms[0], new FormData());
			});
			return existing.concat([session]);
		});
	};

	const updateCommandSession = (
		idx: number,
		session: Promise<borogove.CommandSession> | undefined,
	) => {
		reloadCommands();
		if (session === undefined) {
			setTabIndex(0);
		} else {
			session.then((s) => {
				setTabNames((existing) =>
					existing.map((value, i) => (i === idx ? s.name : value)),
				);
				if (s.forms[0]) logStep(s.forms[0], new FormData());
			});
		}
		setCommandSessions((existing) =>
			existing
				.map((value, i) => (i === idx ? session : value))
				.filter((v) => !!v),
		);
	};

	const logStep = (form: borogove.Form, data: FormData) => {
		const log = getChatExtras(commandsChatId)?.commandLog ?? [];
		const id = crypto.randomUUID();
		const commandLog = [{ id, form, data }, ...log];
		setChatExtras(commandsChatId, { commandLog });
		setLog(commandLog);
	};

	return (
		<ChatContainer className={active ? "active" : undefined}>
			<ConversationHeader>
				<ConversationHeader.Back onClick={() => navigate("../")} />
				<AvatarWithPlaceholder
					as={Avatar}
					photoUri={activeChat.getPhoto()}
					placeholderUri={activeChat.getPlaceholder()}
					displayName={activeChat.getDisplayName()}
				/>
				<ConversationHeader.Content userName={activeChat.getDisplayName()} />
				<ConversationHeader.Actions></ConversationHeader.Actions>
			</ConversationHeader>
			<div as={MessageList} className="cs-message-list">
				<Tabs
					value={tabIndex}
					variant="fullWidth"
					onChange={(e, idx) => setTabIndex(idx)}
				>
					<Tab label="Menu" />
					{commandSessions.map((session, idx) => (
						<Tab key={idx} label={tabNames[idx]} />
					))}
					{log.length > 0 && <Tab label="History" />}
				</Tabs>
				{tabIndex === 0 && (
					<Suspense key={commandsChatId} fallback={<CircularProgress />}>
						<Await resolve={commands}>
							<MenuList autoFocus>
								<CommandMenuInner
									closeMenu={() => {}}
									addSession={addCommandSession}
								/>
							</MenuList>
						</Await>
					</Suspense>
				)}
				{tabIndex > 0 && tabIndex <= commandSessions.length && (
					<CommandSession
						session={commandSessions[tabIndex - 1]}
						updateSession={updateCommandSession}
						logStep={logStep}
						idx={tabIndex - 1}
					/>
				)}
				{tabIndex > commandSessions.length && (
					<div className="command-log">
						{log.map(({ id, ...props }) => (
							<LogItem key={id} {...props} />
						))}
					</div>
				)}
			</div>
		</ChatContainer>
	);
};
